/**
 * Offline Database Schema
 *
 * Uses Dexie.js to provide IndexedDB storage for offline-first functionality.
 * Stores work orders, wasserij items, routes, and a sync queue.
 */

import Dexie, { type Table } from 'dexie';
import type { WerkOrder, WasserijItem, Foto, Route, RoutesObject, GeplandeRit, ProductCatalog } from '../salesforce/types';

// Sync queue item types
export type SyncOperation = 'create' | 'update' | 'delete';
export type SyncEntity = 'WerkOrder' | 'WasserijItem' | 'Foto' | 'GeplandeRit';

export interface SyncQueueItem {
  id?: number;
  entity: SyncEntity;
  operation: SyncOperation;
  recordId: string;
  data: Record<string, unknown>;
  createdAt: Date;
  retryCount: number;
  lastError?: string;
}

// Extended local types with sync metadata
export interface LocalWerkOrder extends WerkOrder {
  _localId?: number;
  _syncStatus: 'synced' | 'pending' | 'conflict';
  _lastSynced?: Date;
  _localModified?: Date;
}

export interface LocalWasserijItem extends WasserijItem {
  _localId?: number;
  _syncStatus: 'synced' | 'pending' | 'conflict';
  _lastSynced?: Date;
  _localModified?: Date;
}

export interface LocalFoto extends Foto {
  _localId?: number;
  _syncStatus: 'synced' | 'pending' | 'conflict';
  _lastSynced?: Date;
  _localBlob?: Blob; // For photos taken offline
}

export interface LocalRoute extends Route {
  _localId?: number;
  _lastSynced?: Date;
}

// New: Routes__c local storage type
export interface LocalRoutesObject extends RoutesObject {
  _localId?: number;
  _syncStatus: 'synced' | 'pending' | 'conflict';
  _lastSynced?: Date;
  _localModified?: Date;
}

// New: Geplande_Rit__c local storage type
export interface LocalGeplandeRit extends GeplandeRit {
  _localId?: number;
  _syncStatus: 'synced' | 'pending' | 'conflict';
  _lastSynced?: Date;
  _localModified?: Date;
  // Denormalized fields for offline display
  _customerName?: string;
  _address?: string;
  _timeWindow?: string;
  _workOrderName?: string;
}

// User preferences stored locally
export interface UserPreferences {
  id: string;
  userId: string;
  defaultRouteView: 'list' | 'map';
  notificationsEnabled: boolean;
  theme: 'light' | 'dark' | 'system';
  lastSync?: Date;
}

// Cached product catalog with TTL
export interface CachedProductCatalog extends ProductCatalog {
  _cacheKey: string; // Always 'productCatalog' (singleton)
  _cachedAt: Date;
  _expiresAt: Date;
}

// Pending item edit for offline support
export type ItemEditAction = 'replacePackage' | 'addExtras' | 'removeExtra';

export interface PendingItemEdit {
  id?: number;
  itemId: string; // Wasserij_Item__c Id
  workOrderId: string; // For grouping/lookup
  action: ItemEditAction;
  payload: {
    newPackageProductId?: string; // For replacePackage
    extraProductIds?: string[]; // For addExtras
    extraItemId?: string; // For removeExtra
  };
  createdAt: Date;
  _syncStatus: 'pending' | 'syncing' | 'failed';
  lastError?: string;
}

// Cached voorinspectie item for offline display
export interface CachedVoorinspectieItem {
  itemId: string;
  itemName: string;
  productName: string;
  werkOrderId: string;
  werkOrderName: string;
  companyCamProjectId: string | null;
  createdDate: Date | string;
  lengthCm: number | null;
  widthCm: number | null;
  diameterCm: number | null;
  surfaceM2: number | null;
  vorm: string | null;
  aantal: number | null;
  soort: string | null;
  merk: string | null;
  wasrapport: string | null;
  secondaryItems: Array<{ itemId: string; productName: string; category: 'Package' | 'Extra' }>;
  isKlacht?: boolean;
  klachtReden?: string | null;
  klantverzoekReparatie?: string | null;
  klantverzoekStatus?: 'Open' | 'Bevestigd' | 'Niet_Nodig' | null;
  klantverzoekBron?: string | null;
  klantverzoekNotitie?: string | null;
  _cachedAt: Date;
}

// Pending voorinspectie for offline queue
export interface PendingVoorinspectie {
  id?: number;
  itemId: string;
  lengthCm?: number;
  widthCm?: number;
  diameterCm?: number;
  isRoundRug?: boolean;
  vorm?: string; // Rechthoek, Rond, Anders
  aantal?: number; // For Anders: quantity
  voorinspectie?: string; // Semicolon-separated values
  soort?: string;
  merk?: string;
  voorinspectieOpmerkingen?: string; // Customer-facing notes
  wasrapport?: string; // Internal notes
  markComplete: boolean;
  createdAt: Date;
  /** 2 = gequeued door de versie die alleen netwerkfouten queuet en ook verstuurt (03-11). Zonder: oude, dode wachtrij. */
  v?: number;
  /** WI-nummer, voor de melding als versturen geweigerd wordt. */
  itemName?: string;
  _syncStatus: 'pending' | 'syncing' | 'failed';
  lastError?: string;
}

// Cached reinigen item for offline display
export interface CachedReinigenItem {
  itemId: string;
  itemName: string;
  productName: string;
  werkOrderId: string;
  werkOrderName: string;
  companyCamProjectId: string | null;
  lengthCm: number | null;
  widthCm: number | null;
  diameterCm: number | null;
  surfaceM2: number | null;
  vorm: string | null; // Rechthoek, Rond, Anders
  voorinspectie: string | null; // Semicolon-separated values
  voorinspectieOpmerkingen: string | null;
  wasrapport: string | null;
  hoeReinigen: string | null;
  secondaryItems: Array<{ itemId: string; productName: string; category: 'Package' | 'Extra' }>;
  isKlacht?: boolean;
  klachtReden?: string | null;
  _cachedAt: Date;
}

// Pending reinigen for offline queue
export interface PendingReinigen {
  id?: number;
  itemId: string;
  hoeReinigen: string; // Required cleaning method
  wasrapport?: string; // Notes to append
  markComplete: boolean;
  createdAt: Date;
  /** 2 = gequeued door de versie die alleen netwerkfouten queuet en ook verstuurt (03-11). Zonder: oude, dode wachtrij. */
  v?: number;
  /** WI-nummer, voor de melding als versturen geweigerd wordt. */
  itemName?: string;
  _syncStatus: 'pending' | 'syncing' | 'failed';
  lastError?: string;
}

// Cached na-inspectie item for offline display
export interface CachedNaInspectieItem {
  itemId: string;
  itemName: string;
  productName: string;
  werkOrderId: string;
  werkOrderName: string;
  status: string; // Current Status__c
  companyCamProjectId: string | null;
  fotosUrl: string | null;
  lengthCm: number | null;
  widthCm: number | null;
  diameterCm: number | null;
  surfaceM2: number | null;
  vorm: string | null;
  voorinspectie: string | null;
  voorinspectieOpmerkingen: string | null;
  wasrapport: string | null;
  secondaryItems: Array<{ itemId: string; productName: string; productCode: string; category: 'Package' | 'Extra' }>;
  isKlacht?: boolean;
  klachtReden?: string | null;
  reparatieNummer?: string | null;
  reparatieStatus?: string | null;
  reparatieOmschrijving?: string | null;
  _cachedAt: Date;
}

// Pending na-inspectie for offline queue
export interface PendingNaInspectie {
  id?: number;
  itemId: string;
  actie: string; // nabehandelen, nogmaals_reinigen, randen_plakken, naar_reparatie, inpakken
  opmerkingenAflevering?: string; // Semicolon-separated values
  opmerkingenAfleveringOverig?: string; // Free text
  naFotosGemaakt?: boolean;
  reden?: 'geur' | 'vlekken' | 'anders';
  redenTekst?: string;
  createdAt: Date;
  /** 2 = gequeued door de versie die alleen netwerkfouten queuet en ook verstuurt (03-11). Zonder: oude, dode wachtrij. */
  v?: number;
  /** WI-nummer, voor de melding als versturen geweigerd wordt. */
  itemName?: string;
  _syncStatus: 'pending' | 'syncing' | 'failed';
  lastError?: string;
}

// Media-laag fase 1: lokale wachtrij voor eigen capture (CameraCapture).
// De blob leeft alleen hier tot de upload slaagt; daarna wordt de rij
// verwijderd zodat er niets op het toestel achterblijft.
export interface MediaQueueItem {
  id?: number;
  /** Oude rijen (t/m 18-08): capture als Blob. iOS Safari bewaart zo'n blob
   *  als verwijzing naar een los bestand en mag dat onder schijfdruk weggooien
   *  terwijl de rij blijft bestaan ("Load failed" bij de upload, data weg).
   *  Nieuwe rijen gebruiken daarom `data`. */
  blob?: Blob;
  /** Capture-bytes inline in de rij (sinds 18-08) — kan niet los evicten. */
  data?: ArrayBuffer;
  mimeType: string;
  type: 'foto' | 'video';
  fase: string; // MediaFase (voorinspectie | reinigen | na-inspectie | klacht | risico | ophaal | aflevering | referentie)
  wiSfId?: string;
  woSfId?: string;
  klachtId?: string;
  /** Status__c van het WI op het schietmoment (bv. 'In Nabehandeling') —
   *  onderscheidt nabehandeling/herwas van de eerste reiniging op de
   *  klant-timeline. Optioneel: alleen gezet waar de pagina het item kent. */
  captureStatus?: string;
  takenAt: Date;
  staffId: string;
  status: 'pending' | 'uploading' | 'failed';
  retryCount: number;
  /** Laatste uploadpoging — voedt de backoff, zodat een blijvend falend item
   *  de ronde niet elke 30 seconden opnieuw opvreet. Niet geïndexeerd. */
  lastAttemptAt?: Date;
  lastError?: string;
  createdAt: Date;
}

class KledaFieldDatabase extends Dexie {
  // Tables
  werkOrders!: Table<LocalWerkOrder, string>;
  wasserijItems!: Table<LocalWasserijItem, string>;
  fotos!: Table<LocalFoto, string>;
  routes!: Table<LocalRoute, string>;
  routesObjects!: Table<LocalRoutesObject, string>; // Routes__c
  geplandeRitten!: Table<LocalGeplandeRit, string>; // Geplande_Rit__c
  syncQueue!: Table<SyncQueueItem, number>;
  preferences!: Table<UserPreferences, string>;
  productCatalog!: Table<CachedProductCatalog, string>; // Product catalog cache
  pendingItemEdits!: Table<PendingItemEdit, number>; // Offline item edits
  voorinspectieItems!: Table<CachedVoorinspectieItem, string>; // Cached voorinspectie items
  pendingVoorinspecties!: Table<PendingVoorinspectie, number>; // Offline voorinspectie queue
  reinigenItems!: Table<CachedReinigenItem, string>; // Cached reinigen items
  pendingReinigen!: Table<PendingReinigen, number>; // Offline reinigen queue
  naInspectieItems!: Table<CachedNaInspectieItem, string>; // Cached na-inspectie items
  pendingNaInspecties!: Table<PendingNaInspectie, number>; // Offline na-inspectie queue
  mediaQueue!: Table<MediaQueueItem, number>; // Media-laag: offline upload queue (blob tot upload)

  constructor() {
    super('KledaFieldDB');

    // Schema definition
    // The first column is the primary key
    // '++' means auto-incrementing
    // '&' means unique
    // '*' means multi-entry index (for arrays)

    // Version 1 - Original schema
    this.version(1).stores({
      werkOrders: 'Id, Name, Werk_Order_Status__c, _syncStatus, _lastSynced',
      wasserijItems: 'Id, Werk_Order__c, _syncStatus, _lastSynced',
      fotos: 'Id, Werk_Order__c, Wasserij_item__c, Categorie__c, _syncStatus',
      routes: 'id, date, type, _lastSynced',
      syncQueue: '++id, entity, recordId, createdAt',
      preferences: 'id, userId',
    });

    // Version 2 - Add Routes__c and Geplande_Rit__c tables
    this.version(2).stores({
      werkOrders: 'Id, Name, Werk_Order_Status__c, _syncStatus, _lastSynced',
      wasserijItems: 'Id, Werk_Order__c, _syncStatus, _lastSynced',
      fotos: 'Id, Werk_Order__c, Wasserij_item__c, Categorie__c, _syncStatus',
      routes: 'id, date, type, _lastSynced',
      routesObjects: 'Id, Chauffeur__c, Datum__c, _syncStatus, _lastSynced',
      geplandeRitten: 'Id, Geplande_Route__c, Werk_Order__c, Route_Sequence__c, Status__c, Type__c, Chauffeur__c, _syncStatus, _lastSynced',
      syncQueue: '++id, entity, recordId, createdAt',
      preferences: 'id, userId',
    });

    // Version 3 - Add productCatalog table for caching
    this.version(3).stores({
      werkOrders: 'Id, Name, Werk_Order_Status__c, _syncStatus, _lastSynced',
      wasserijItems: 'Id, Werk_Order__c, _syncStatus, _lastSynced',
      fotos: 'Id, Werk_Order__c, Wasserij_item__c, Categorie__c, _syncStatus',
      routes: 'id, date, type, _lastSynced',
      routesObjects: 'Id, Chauffeur__c, Datum__c, _syncStatus, _lastSynced',
      geplandeRitten: 'Id, Geplande_Route__c, Werk_Order__c, Route_Sequence__c, Status__c, Type__c, Chauffeur__c, _syncStatus, _lastSynced',
      syncQueue: '++id, entity, recordId, createdAt',
      preferences: 'id, userId',
      productCatalog: '_cacheKey, _expiresAt',
    });

    // Version 4 - Add pendingItemEdits table for offline item editing
    this.version(4).stores({
      werkOrders: 'Id, Name, Werk_Order_Status__c, _syncStatus, _lastSynced',
      wasserijItems: 'Id, Werk_Order__c, _syncStatus, _lastSynced',
      fotos: 'Id, Werk_Order__c, Wasserij_item__c, Categorie__c, _syncStatus',
      routes: 'id, date, type, _lastSynced',
      routesObjects: 'Id, Chauffeur__c, Datum__c, _syncStatus, _lastSynced',
      geplandeRitten: 'Id, Geplande_Route__c, Werk_Order__c, Route_Sequence__c, Status__c, Type__c, Chauffeur__c, _syncStatus, _lastSynced',
      syncQueue: '++id, entity, recordId, createdAt',
      preferences: 'id, userId',
      productCatalog: '_cacheKey, _expiresAt',
      pendingItemEdits: '++id, itemId, workOrderId, action, _syncStatus, createdAt',
    });

    // Version 5 - Add voorinspectie tables
    this.version(5).stores({
      werkOrders: 'Id, Name, Werk_Order_Status__c, _syncStatus, _lastSynced',
      wasserijItems: 'Id, Werk_Order__c, _syncStatus, _lastSynced',
      fotos: 'Id, Werk_Order__c, Wasserij_item__c, Categorie__c, _syncStatus',
      routes: 'id, date, type, _lastSynced',
      routesObjects: 'Id, Chauffeur__c, Datum__c, _syncStatus, _lastSynced',
      geplandeRitten: 'Id, Geplande_Route__c, Werk_Order__c, Route_Sequence__c, Status__c, Type__c, Chauffeur__c, _syncStatus, _lastSynced',
      syncQueue: '++id, entity, recordId, createdAt',
      preferences: 'id, userId',
      productCatalog: '_cacheKey, _expiresAt',
      pendingItemEdits: '++id, itemId, workOrderId, action, _syncStatus, createdAt',
      voorinspectieItems: 'itemId, werkOrderId, _cachedAt',
      pendingVoorinspecties: '++id, itemId, _syncStatus, createdAt',
    });

    // Version 6 - Add reinigen tables
    this.version(6).stores({
      werkOrders: 'Id, Name, Werk_Order_Status__c, _syncStatus, _lastSynced',
      wasserijItems: 'Id, Werk_Order__c, _syncStatus, _lastSynced',
      fotos: 'Id, Werk_Order__c, Wasserij_item__c, Categorie__c, _syncStatus',
      routes: 'id, date, type, _lastSynced',
      routesObjects: 'Id, Chauffeur__c, Datum__c, _syncStatus, _lastSynced',
      geplandeRitten: 'Id, Geplande_Route__c, Werk_Order__c, Route_Sequence__c, Status__c, Type__c, Chauffeur__c, _syncStatus, _lastSynced',
      syncQueue: '++id, entity, recordId, createdAt',
      preferences: 'id, userId',
      productCatalog: '_cacheKey, _expiresAt',
      pendingItemEdits: '++id, itemId, workOrderId, action, _syncStatus, createdAt',
      voorinspectieItems: 'itemId, werkOrderId, _cachedAt',
      pendingVoorinspecties: '++id, itemId, _syncStatus, createdAt',
      reinigenItems: 'itemId, werkOrderId, _cachedAt',
      pendingReinigen: '++id, itemId, _syncStatus, createdAt',
    });

    // Version 7 - Add na-inspectie tables
    this.version(7).stores({
      werkOrders: 'Id, Name, Werk_Order_Status__c, _syncStatus, _lastSynced',
      wasserijItems: 'Id, Werk_Order__c, _syncStatus, _lastSynced',
      fotos: 'Id, Werk_Order__c, Wasserij_item__c, Categorie__c, _syncStatus',
      routes: 'id, date, type, _lastSynced',
      routesObjects: 'Id, Chauffeur__c, Datum__c, _syncStatus, _lastSynced',
      geplandeRitten: 'Id, Geplande_Route__c, Werk_Order__c, Route_Sequence__c, Status__c, Type__c, Chauffeur__c, _syncStatus, _lastSynced',
      syncQueue: '++id, entity, recordId, createdAt',
      preferences: 'id, userId',
      productCatalog: '_cacheKey, _expiresAt',
      pendingItemEdits: '++id, itemId, workOrderId, action, _syncStatus, createdAt',
      voorinspectieItems: 'itemId, werkOrderId, _cachedAt',
      pendingVoorinspecties: '++id, itemId, _syncStatus, createdAt',
      reinigenItems: 'itemId, werkOrderId, _cachedAt',
      pendingReinigen: '++id, itemId, _syncStatus, createdAt',
      naInspectieItems: 'itemId, werkOrderId, _cachedAt',
      pendingNaInspecties: '++id, itemId, _syncStatus, createdAt',
    });

    // Version 8 - Media-laag fase 1: mediaQueue voor eigen capture-uploads
    this.version(8).stores({
      werkOrders: 'Id, Name, Werk_Order_Status__c, _syncStatus, _lastSynced',
      wasserijItems: 'Id, Werk_Order__c, _syncStatus, _lastSynced',
      fotos: 'Id, Werk_Order__c, Wasserij_item__c, Categorie__c, _syncStatus',
      routes: 'id, date, type, _lastSynced',
      routesObjects: 'Id, Chauffeur__c, Datum__c, _syncStatus, _lastSynced',
      geplandeRitten: 'Id, Geplande_Route__c, Werk_Order__c, Route_Sequence__c, Status__c, Type__c, Chauffeur__c, _syncStatus, _lastSynced',
      syncQueue: '++id, entity, recordId, createdAt',
      preferences: 'id, userId',
      productCatalog: '_cacheKey, _expiresAt',
      pendingItemEdits: '++id, itemId, workOrderId, action, _syncStatus, createdAt',
      voorinspectieItems: 'itemId, werkOrderId, _cachedAt',
      pendingVoorinspecties: '++id, itemId, _syncStatus, createdAt',
      reinigenItems: 'itemId, werkOrderId, _cachedAt',
      pendingReinigen: '++id, itemId, _syncStatus, createdAt',
      naInspectieItems: 'itemId, werkOrderId, _cachedAt',
      pendingNaInspecties: '++id, itemId, _syncStatus, createdAt',
      mediaQueue: '++id, status, wiSfId, woSfId, createdAt',
    });
  }
}

// Create and export singleton instance
export const db = new KledaFieldDatabase();

// Database utility functions
export async function clearAllData(): Promise<void> {
  await db.werkOrders.clear();
  await db.wasserijItems.clear();
  await db.fotos.clear();
  await db.routes.clear();
  await db.routesObjects.clear();
  await db.geplandeRitten.clear();
  await db.syncQueue.clear();
  await db.pendingItemEdits.clear();
  await db.voorinspectieItems.clear();
  await db.pendingVoorinspecties.clear();
  await db.reinigenItems.clear();
  await db.pendingReinigen.clear();
  await db.naInspectieItems.clear();
  await db.pendingNaInspecties.clear();
  // Keep preferences
}

export async function clearSyncQueue(): Promise<void> {
  await db.syncQueue.clear();
}

export async function getSyncQueueCount(): Promise<number> {
  return db.syncQueue.count();
}

export async function addToSyncQueue(item: Omit<SyncQueueItem, 'id' | 'createdAt' | 'retryCount'>): Promise<number> {
  return db.syncQueue.add({
    ...item,
    createdAt: new Date(),
    retryCount: 0,
  });
}

export async function getNextSyncItem(): Promise<SyncQueueItem | undefined> {
  return db.syncQueue.orderBy('createdAt').first();
}

export async function removeSyncItem(id: number): Promise<void> {
  await db.syncQueue.delete(id);
}

export async function incrementSyncRetry(id: number, error: string): Promise<void> {
  await db.syncQueue.update(id, {
    retryCount: (await db.syncQueue.get(id))?.retryCount ?? 0 + 1,
    lastError: error,
  });
}

// ============================================
// Pending Item Edits Helper Functions
// ============================================

/**
 * Add a pending item edit for offline sync
 */
export async function addPendingItemEdit(
  edit: Omit<PendingItemEdit, 'id' | 'createdAt' | '_syncStatus'>
): Promise<number> {
  return db.pendingItemEdits.add({
    ...edit,
    createdAt: new Date(),
    _syncStatus: 'pending',
  });
}

/**
 * Get all pending item edits
 */
export async function getPendingItemEdits(): Promise<PendingItemEdit[]> {
  return db.pendingItemEdits.where('_syncStatus').equals('pending').toArray();
}

/**
 * Get pending edits for a specific item
 */
export async function getPendingEditsForItem(itemId: string): Promise<PendingItemEdit[]> {
  return db.pendingItemEdits.where('itemId').equals(itemId).toArray();
}

/**
 * Get pending edits for a work order
 */
export async function getPendingEditsForWorkOrder(workOrderId: string): Promise<PendingItemEdit[]> {
  return db.pendingItemEdits.where('workOrderId').equals(workOrderId).toArray();
}

/**
 * Update edit status (for sync progress)
 */
export async function updatePendingEditStatus(
  id: number,
  status: PendingItemEdit['_syncStatus'],
  error?: string
): Promise<void> {
  await db.pendingItemEdits.update(id, {
    _syncStatus: status,
    ...(error ? { lastError: error } : {}),
  });
}

/**
 * Remove a pending item edit after successful sync
 */
export async function removePendingItemEdit(id: number): Promise<void> {
  await db.pendingItemEdits.delete(id);
}

/**
 * Get count of pending item edits
 */
export async function getPendingItemEditsCount(): Promise<number> {
  return db.pendingItemEdits.where('_syncStatus').equals('pending').count();
}

/**
 * Clear all pending item edits (e.g., after logout)
 */
export async function clearPendingItemEdits(): Promise<void> {
  await db.pendingItemEdits.clear();
}

// Hook for database ready state
export function useDatabaseReady(): boolean {
  // Dexie handles this automatically - the database is ready when imported
  return true;
}

// ============================================
// Routes__c and Geplande_Rit__c Helper Functions
// ============================================

/**
 * Get routes for a specific date and driver
 */
export async function getRoutesForDate(
  date: string,
  chauffeurId?: string
): Promise<LocalRoutesObject[]> {
  const query = db.routesObjects.where('Datum__c').equals(date);
  const routes = await query.toArray();

  if (chauffeurId) {
    return routes.filter(r => r.Chauffeur__c === chauffeurId);
  }
  return routes;
}

/**
 * Get all geplande ritten for a route, ordered by sequence
 */
export async function getGeplandeRittenForRoute(
  routeId: string
): Promise<LocalGeplandeRit[]> {
  return db.geplandeRitten
    .where('Geplande_Route__c')
    .equals(routeId)
    .sortBy('Route_Sequence__c');
}

/**
 * Get a single geplande rit by ID
 */
export async function getGeplandeRit(ritId: string): Promise<LocalGeplandeRit | undefined> {
  return db.geplandeRitten.get(ritId);
}

/**
 * Update geplande rit status (for offline completion)
 */
export async function updateGeplandeRitStatus(
  ritId: string,
  status: 'Ingepland' | 'Verwerkt',
  syncStatus: 'synced' | 'pending' = 'pending'
): Promise<void> {
  await db.geplandeRitten.update(ritId, {
    Status__c: status,
    _syncStatus: syncStatus,
    _localModified: new Date(),
  });
}

/**
 * Save routes from API to local database
 */
export async function saveRoutesToLocal(routes: LocalRoutesObject[]): Promise<void> {
  await db.routesObjects.bulkPut(routes);
}

/**
 * Save geplande ritten from API to local database
 */
export async function saveGeplandeRittenToLocal(ritten: LocalGeplandeRit[]): Promise<void> {
  await db.geplandeRitten.bulkPut(ritten);
}

/**
 * Get pending sync items for geplande ritten
 */
export async function getPendingGeplandeRitten(): Promise<LocalGeplandeRit[]> {
  return db.geplandeRitten.where('_syncStatus').equals('pending').toArray();
}

/**
 * Get stats for today's routes
 */
export async function getDailyStats(
  date: string,
  chauffeurId: string
): Promise<{
  totalStops: number;
  completedStops: number;
  pendingStops: number;
  skippedStops: number;
}> {
  const routes = await getRoutesForDate(date, chauffeurId);
  const routeIds = routes.map(r => r.Id);

  const allRitten = await db.geplandeRitten
    .where('Geplande_Route__c')
    .anyOf(routeIds)
    .toArray();

  return {
    totalStops: allRitten.length,
    completedStops: allRitten.filter(r => r.Status__c === 'Verwerkt').length,
    pendingStops: allRitten.filter(r => r.Status__c === 'Ingepland' && !r.Overgeslagen__c).length,
    skippedStops: allRitten.filter(r => r.Overgeslagen__c === true).length,
  };
}

// ============================================
// Product Catalog Cache Functions
// ============================================

const PRODUCT_CATALOG_CACHE_KEY = 'productCatalog';
const PRODUCT_CATALOG_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Get cached product catalog if still valid
 * Returns undefined if cache is expired or not found
 */
export async function getCachedProductCatalog(): Promise<CachedProductCatalog | undefined> {
  const cached = await db.productCatalog.get(PRODUCT_CATALOG_CACHE_KEY);

  if (!cached) {
    return undefined;
  }

  // Check if cache is expired
  const now = new Date();
  if (cached._expiresAt < now) {
    // Cache expired, remove it
    await db.productCatalog.delete(PRODUCT_CATALOG_CACHE_KEY);
    return undefined;
  }

  return cached;
}

/**
 * Save product catalog to cache with 24-hour TTL
 */
export async function cacheProductCatalog(catalog: ProductCatalog): Promise<void> {
  const now = new Date();
  const expiresAt = new Date(now.getTime() + PRODUCT_CATALOG_TTL_MS);

  const cachedCatalog: CachedProductCatalog = {
    ...catalog,
    _cacheKey: PRODUCT_CATALOG_CACHE_KEY,
    _cachedAt: now,
    _expiresAt: expiresAt,
  };

  await db.productCatalog.put(cachedCatalog);
}

/**
 * Clear product catalog cache (force refresh on next fetch)
 */
export async function clearProductCatalogCache(): Promise<void> {
  await db.productCatalog.delete(PRODUCT_CATALOG_CACHE_KEY);
}

/**
 * Check if product catalog cache is valid
 */
export async function isProductCatalogCacheValid(): Promise<boolean> {
  const cached = await db.productCatalog.get(PRODUCT_CATALOG_CACHE_KEY);

  if (!cached) {
    return false;
  }

  return cached._expiresAt > new Date();
}

/**
 * Get product catalog cache age in hours (for UI display)
 */
export async function getProductCatalogCacheAge(): Promise<number | null> {
  const cached = await db.productCatalog.get(PRODUCT_CATALOG_CACHE_KEY);

  if (!cached) {
    return null;
  }

  const now = new Date();
  const ageMs = now.getTime() - cached._cachedAt.getTime();
  return Math.round(ageMs / (60 * 60 * 1000)); // Hours
}

// ============================================
// Voorinspectie Cache Functions
// ============================================

const VOORINSPECTIE_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

/**
 * Get cached voorinspectie items if still valid
 */
export async function getCachedVoorinspectieItems(): Promise<CachedVoorinspectieItem[]> {
  const items = await db.voorinspectieItems.toArray();

  if (items.length === 0) {
    return [];
  }

  // Check if cache is expired (based on first item)
  const now = new Date();
  const cacheAge = now.getTime() - (items[0]?._cachedAt.getTime() ?? 0);

  if (cacheAge > VOORINSPECTIE_CACHE_TTL_MS) {
    // Cache expired, clear it
    await db.voorinspectieItems.clear();
    return [];
  }

  return items;
}

/**
 * Cache voorinspectie items
 */
export async function cacheVoorinspectieItems(items: CachedVoorinspectieItem[]): Promise<void> {
  const now = new Date();

  // Clear old cache
  await db.voorinspectieItems.clear();

  // Add new items with timestamp
  const itemsWithTimestamp = items.map(item => ({
    ...item,
    _cachedAt: now,
  }));

  await db.voorinspectieItems.bulkPut(itemsWithTimestamp);
}

/**
 * Remove an item from voorinspectie cache (after completion)
 */
export async function removeFromVoorinspectieCache(itemId: string): Promise<void> {
  await db.voorinspectieItems.delete(itemId);
}

/** Wachtrij-rijen met deze versie worden verstuurd (lib/sync/wasserij-wachtrij.ts); oudere worden opgeruimd. */
export const WACHTRIJ_VERSIE = 2;

/**
 * Add a pending voorinspectie to the offline queue
 */
export async function queuePendingVoorinspectie(
  data: Omit<PendingVoorinspectie, 'id' | 'createdAt' | '_syncStatus'>
): Promise<number> {
  return db.pendingVoorinspecties.add({
    ...data,
    v: WACHTRIJ_VERSIE,
    createdAt: new Date(),
    _syncStatus: 'pending',
  });
}

/**
 * Get all pending voorinspecties for sync
 */
export async function getPendingVoorinspecties(): Promise<PendingVoorinspectie[]> {
  return db.pendingVoorinspecties
    .where('_syncStatus')
    .equals('pending')
    .toArray();
}

/**
 * Mark a pending voorinspectie as synced (removes it)
 */
export async function markVoorinspectieSynced(id: number): Promise<void> {
  await db.pendingVoorinspecties.delete(id);
}

/**
 * Mark a pending voorinspectie as failed
 */
export async function markVoorinspectieFailed(id: number, error: string): Promise<void> {
  await db.pendingVoorinspecties.update(id, {
    _syncStatus: 'failed',
    lastError: error,
  });
}

// ============================================
// Reinigen Cache Functions
// ============================================

const REINIGEN_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

/**
 * Get cached reinigen items if still valid
 */
export async function getCachedReinigenItems(): Promise<CachedReinigenItem[]> {
  const items = await db.reinigenItems.toArray();

  if (items.length === 0) {
    return [];
  }

  // Check if cache is expired (based on first item)
  const now = new Date();
  const cacheAge = now.getTime() - (items[0]?._cachedAt.getTime() ?? 0);

  if (cacheAge > REINIGEN_CACHE_TTL_MS) {
    // Cache expired, clear it
    await db.reinigenItems.clear();
    return [];
  }

  return items;
}

/**
 * Cache reinigen items
 */
export async function cacheReinigenItems(items: CachedReinigenItem[]): Promise<void> {
  const now = new Date();

  // Clear old cache
  await db.reinigenItems.clear();

  // Add new items with timestamp
  const itemsWithTimestamp = items.map(item => ({
    ...item,
    _cachedAt: now,
  }));

  await db.reinigenItems.bulkPut(itemsWithTimestamp);
}

/**
 * Remove an item from reinigen cache (after completion)
 */
export async function removeCachedReinigenItem(itemId: string): Promise<void> {
  await db.reinigenItems.delete(itemId);
}

/**
 * Add a pending reinigen to the offline queue
 */
export async function queuePendingReinigen(
  data: Omit<PendingReinigen, 'id' | 'createdAt' | '_syncStatus'>
): Promise<number> {
  return db.pendingReinigen.add({
    ...data,
    v: WACHTRIJ_VERSIE,
    createdAt: new Date(),
    _syncStatus: 'pending',
  });
}

/**
 * Get all pending reinigen for sync
 */
export async function getPendingReinigen(): Promise<PendingReinigen[]> {
  return db.pendingReinigen
    .where('_syncStatus')
    .equals('pending')
    .toArray();
}

/**
 * Mark a pending reinigen as synced (removes it)
 */
export async function markReinigenSynced(id: number): Promise<void> {
  await db.pendingReinigen.delete(id);
}

/**
 * Mark a pending reinigen as failed
 */
export async function markReinigenFailed(id: number, error: string): Promise<void> {
  await db.pendingReinigen.update(id, {
    _syncStatus: 'failed',
    lastError: error,
  });
}

// ============================================
// Na-inspectie Cache Functions
// ============================================

const NA_INSPECTIE_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

/**
 * Get cached na-inspectie items if still valid
 */
export async function getCachedNaInspectieItems(): Promise<CachedNaInspectieItem[]> {
  const items = await db.naInspectieItems.toArray();

  if (items.length === 0) {
    return [];
  }

  // Check if cache is expired (based on first item)
  const firstItem = items[0];
  if (!firstItem) {
    return [];
  }

  const now = new Date();
  const cacheAge = now.getTime() - firstItem._cachedAt.getTime();

  if (cacheAge > NA_INSPECTIE_CACHE_TTL_MS) {
    // Cache expired, clear it
    await db.naInspectieItems.clear();
    return [];
  }

  return items;
}

/**
 * Cache na-inspectie items
 */
export async function cacheNaInspectieItems(items: CachedNaInspectieItem[]): Promise<void> {
  const now = new Date();

  // Clear old cache
  await db.naInspectieItems.clear();

  // Add new items with timestamp
  const itemsWithTimestamp = items.map(item => ({
    ...item,
    _cachedAt: now,
  }));

  await db.naInspectieItems.bulkPut(itemsWithTimestamp);
}

/**
 * Remove an item from na-inspectie cache (after completion)
 */
export async function removeFromNaInspectieCache(itemId: string): Promise<void> {
  await db.naInspectieItems.delete(itemId);
}

/**
 * Add a pending na-inspectie to the offline queue
 */
export async function queuePendingNaInspectie(
  data: Omit<PendingNaInspectie, 'id' | 'createdAt' | '_syncStatus'>
): Promise<number> {
  return db.pendingNaInspecties.add({
    ...data,
    v: WACHTRIJ_VERSIE,
    createdAt: new Date(),
    _syncStatus: 'pending',
  });
}

/**
 * Get all pending na-inspecties for sync
 */
export async function getPendingNaInspecties(): Promise<PendingNaInspectie[]> {
  return db.pendingNaInspecties
    .where('_syncStatus')
    .equals('pending')
    .toArray();
}

/**
 * Mark a pending na-inspectie as synced (removes it)
 */
export async function markNaInspectieSynced(id: number): Promise<void> {
  await db.pendingNaInspecties.delete(id);
}

/**
 * Mark a pending na-inspectie as failed
 */
export async function markNaInspectieFailed(id: number, error: string): Promise<void> {
  await db.pendingNaInspecties.update(id, {
    _syncStatus: 'failed',
    lastError: error,
  });
}
